import React, { createContext, useContext, useMemo, useState } from 'react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import { EditorInterface, TourContext } from 'data/intros/TourContext';
import { MultiPageGuideCollection } from 'data/MultiPageGuideCollection';
import { StepWithAction } from 'data/StepWithAction';
import introJs, { Options, Step } from 'intro.js';
import { useNavigate } from 'react-router-dom';


interface GlobalTourProviderProps {
  children: React.ReactNode;
  guidesCollection: MultiPageGuideCollection[];
}

interface TourContextType {
  continueTourAt: (guideKey: string, stageNumber: number, delayMs: number) => void;
  navigateToNextStage: (toUrl: string, key: string, stage: number) => void;
}

const GlobalTourContext = createContext<TourContextType | undefined>({
  continueTourAt: (_: string, __: number, ___: number) => { },
  navigateToNextStage: (_: string, __: string, ___: number) => { }
});

const GlobalTourProvider: React.FC<GlobalTourProviderProps> = ({ children, guidesCollection }) => {
  const [guides] = useState<MultiPageGuideCollection[]>(guidesCollection);
  const { notify } = useContext(AppContext);
  const navigate = useNavigate();
  const defaultSettings: Options = {
    disableInteraction: false,
    showStepNumbers: false,
    showBullets: false,
    showProgress: false,
    scrollToElement: true,
    exitOnOverlayClick: false,
    exitOnEsc: false,
    overlayOpacity: .25,
    highlightClass: 'guided-tour'
  }

  const getGuideByKey = (key: string): MultiPageGuideCollection | undefined => guides.find(g => g.key === key);

  const navigateToNextStage = (toUrl: string, key: string, stage: number): void => {
    const { args } = (window as any as TourContext<EditorInterface>);
    let url = toUrl;
    if (args) {
      url = toUrl.replaceAll(':documentId', args.documentId?.toString() ?? '');
    }

    navigate(`${url}?gn.key=${key}&gn.stage_number=${stage}`);
  }

  const onBeforeChangeHandler = (intro: introJs.IntroJs, continueSteps: Step[], isFinalStep = false) => {
    if (!intro) {
      throw new Error('intro is undefined');
    }

    const desiredStepIndex = intro.currentStep() ?? 0;
    if (desiredStepIndex === 0) {
      return true;
    }

    const index = isFinalStep ? desiredStepIndex : desiredStepIndex - 1;
    const currentStep = continueSteps[index] as StepWithAction;
    if (currentStep && currentStep.onBeforeNextStep) {
      currentStep.onBeforeNextStep();
    }

    if (currentStep.disableInteraction) {
      intro.setOption('disableInteraction', true);
    } else {
      intro.setOption('disableInteraction', false);
    }

    if (currentStep && (currentStep as StepWithAction).isActionCompleted) {
      const { isCompleted, message } = (currentStep as StepWithAction).isActionCompleted();;
      const userMessage = message ?? 'Please complete the required action before proceeding to the next step.';
      if (!isCompleted) {
        notify(userMessage, NotificationType.Warning);
        return false;
      }
    }
    return true;
  }

  const onShouldExitHandler = (shouldContinue: boolean, guide: MultiPageGuideCollection, currentStageNumber: number) => {
    if (shouldContinue) {
      const indexOfCurrentStage = guide!.steps.findIndex(s => s.stageNumber === currentStageNumber);
      if (indexOfCurrentStage < guide!.steps.length) {
        const nextStage = guide!.steps[indexOfCurrentStage + 1];
        if (nextStage) {
          navigateToNextStage(nextStage.navigateTo, guide!.key, nextStage.stageNumber);
        } else if (guide.onCompleteNavigateTo) {
          navigate(guide.onCompleteNavigateTo);
        }
      }
      return true;
    }
    return false;
  }

  const continueTourAt = async (guideKey: string, stageNumber: number, delayMs = 250) => {

    const guide = getGuideByKey(guideKey);
    if (!guide) {
      throw new Error(`failed to find guide ${guideKey} to continue tour`);
    }

    const guideStage = guide.steps.find(s => s.stageNumber === stageNumber);
    if (!guideStage) {
      throw new Error(`could not find stage number ${stageNumber} in guide ${guideKey} `);
    }

    if (!window.location.href.includes(guideStage.navigateTo)) {
      navigateToNextStage(guideStage.navigateTo, guide.key, stageNumber);
    }

    const continueSteps = guideStage.steps;

    const nextIntro = introJs();

    nextIntro.setOptions({
      steps: continueSteps,
      ...defaultSettings
    });

    nextIntro.onbeforechange((_: HTMLElement): boolean => onBeforeChangeHandler(nextIntro, continueSteps));

    nextIntro.onbeforeexit(() => {
      if (!guideStage) {
        throw new Error('continueAtStage is undefined');
      }
      const shouldContinue = onBeforeChangeHandler(nextIntro, guideStage.steps, true);
      return onShouldExitHandler(shouldContinue, guide, guideStage.stageNumber);
    });

    setTimeout(() => {
      nextIntro.start();
    }, delayMs);

    (window as any).introContext = nextIntro;

  };

  const contextValue: TourContextType = useMemo(() => ({
    continueTourAt,
    navigateToNextStage
  }), [continueTourAt, navigateToNextStage]);

  return (
    <GlobalTourContext.Provider value={contextValue}>
      {children}
    </GlobalTourContext.Provider>
  );
}

export { GlobalTourProvider, GlobalTourContext };